import { useState } from "react";
import axios from "axios";
import AlertDialog from "./AlertDialog";
const API = "https://erp-xe9u.onrender.com/api";

export default function EmailForm({
  proposal,
  setShowEmailForm,
  pdfBuffer,
  setPdfBuffer,
  setCurrentProposal,
  userEmail,
  companyProfile,
  opportunity,
  accounts,
  contacts,
}) {
  const [emailForm, setEmailForm] = useState({
    to: contacts.find((c) => c._id === opportunity?.contact)?.email || "",
    subject: `Proposal: ${proposal.title} - ${opportunity?.title}`,
    body: `Dear ${
      contacts.find((c) => c._id === opportunity?.contact)?.firstName ||
      "Client"
    },\n\nPlease find attached the proposal for your review.\n\nBest regards,`,
    signature: companyProfile?.companyName || "Your Company Name",
  });
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const handleEmailSubmit = async () => {
    try {
      if (!emailForm.to || !emailForm.subject || !emailForm.body) {
        setAlert({
          open: true,
          type: "error",
          title: "Invalid Input",
          message: "Please fill in all required email fields",
          onConfirm: () => setAlert({ ...alert, open: false }),
        });
        return;
      }

      const validateEmails = (emails) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails
          .split(",")
          .map((email) => email.trim())
          .every((email) => emailRegex.test(email));
      };

      if (!validateEmails(emailForm.to)) {
        setAlert({
          open: true,
          type: "error",
          title: "Invalid Email",
          message: "Please enter valid email addresses",
          onConfirm: () => setAlert({ ...alert, open: false }),
        });
        return;
      }

      if (
        !(pdfBuffer instanceof Blob) ||
        pdfBuffer.type !== "application/pdf"
      ) {
        setAlert({
          open: true,
          type: "error",
          title: "Missing or Invalid Attachment",
          message: "A valid PDF file is required",
          onConfirm: () => setAlert({ ...alert, open: false }),
        });
        return;
      }

      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("to", emailForm.to);
      formData.append("subject", emailForm.subject);
      formData.append("body", emailForm.body);
      formData.append("signature", emailForm.signature || "");
      formData.append("from", userEmail);
      formData.append("files", pdfBuffer, `Proposal_${proposal.title}.pdf`);

      const response = await axios.post(
        `${API}/proposals/${proposal._id}/send-email`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setShowEmailForm(false);
      setPdfBuffer(null);
      setCurrentProposal(null);
      if (response.status === 200) {
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "Email sent successfully!",
          onConfirm: () => setAlert({ ...alert, open: false }),
        });
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message:
          "Failed to send email: " +
          (err.response?.data?.message || err.message),
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg">
        <h2 className="text-xl font-semibold mb-4">Send Email</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              From
            </label>
            <input
              type="email"
              value={userEmail}
              disabled
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              To
            </label>
            <input
              type="text"
              value={emailForm.to}
              onChange={(e) =>
                setEmailForm({ ...emailForm, to: e.target.value })
              }
              placeholder="recipient@example.com, another@example.com"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Subject
            </label>
            <input
              type="text"
              value={emailForm.subject}
              onChange={(e) =>
                setEmailForm({ ...emailForm, subject: e.target.value })
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Body
            </label>
            <textarea
              value={emailForm.body}
              onChange={(e) =>
                setEmailForm({ ...emailForm, body: e.target.value })
              }
              rows="5"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Signature
            </label>
            <textarea
              value={emailForm.signature}
              onChange={(e) =>
                setEmailForm({ ...emailForm, signature: e.target.value })
              }
              rows="3"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => {
                setShowEmailForm(false);
                setPdfBuffer(null);
                setCurrentProposal(null);
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleEmailSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
              disabled={!pdfBuffer}
            >
              Send Email
            </button>
          </div>
        </div>
      </div>
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
        onConfirm={alert.onConfirm}
      />
    </div>
  );
}
